/**
 * Zod mirrors of the vocabulary, for the routes that carry it. They live here
 * rather than in api-contract so the wire and the domain cannot drift: each
 * enum schema is built FROM the const tuple it mirrors, and the `satisfies`
 * checks at the bottom fail to compile if a type and its schema part ways.
 */

import { z } from 'zod';
import { RECORD_KINDS, type RecordKind } from './records.js';
import { INCIDENT_SEVERITIES, INCIDENT_SOURCES, INCIDENT_STATUSES, type IncidentSeverity, type IncidentStatus } from './incident.js';
import { CHANGE_RISKS, CHANGE_STATUSES, CHANGE_TYPES, type ChangeStatus, type ChangeType, type FreezeVerdict } from './change.js';
import { REQUEST_CATEGORIES, REQUEST_STATUSES, type RequestStatus } from './request.js';
import { IMPACT_RESOURCE_TYPES, IMPACT_TYPES, SERVICE_TIERS } from './impact.js';
import { LINK_RELATIONSHIPS, LINK_TARGET_KINDS } from './links.js';
import { ACTOR_TYPES, TIMELINE_EVENT_TYPES } from './timeline.js';
import { SIGNAL_STATUSES } from './signals.js';
import type { GuardVerdict, LiveExposure } from './guard.js';
import { PUBLIC_COMPONENT_IDS, PUBLIC_IMPACTS, PUBLIC_STATES, PUBLIC_STATUSES } from './public.js';

export const RecordKindSchema = z.enum(RECORD_KINDS);
export const IncidentSeveritySchema = z.enum(INCIDENT_SEVERITIES);
export const IncidentStatusSchema = z.enum(INCIDENT_STATUSES);
export const IncidentSourceSchema = z.enum(INCIDENT_SOURCES);
export const ChangeTypeSchema = z.enum(CHANGE_TYPES);
export const ChangeRiskSchema = z.enum(CHANGE_RISKS);
export const ChangeStatusSchema = z.enum(CHANGE_STATUSES);
export const RequestStatusSchema = z.enum(REQUEST_STATUSES);
export const RequestCategorySchema = z.enum(REQUEST_CATEGORIES);
export const ImpactResourceTypeSchema = z.enum(IMPACT_RESOURCE_TYPES);
export const ImpactTypeSchema = z.enum(IMPACT_TYPES);
export const ServiceTierSchema = z.enum(SERVICE_TIERS);
export const LinkRelationshipSchema = z.enum(LINK_RELATIONSHIPS);
export const LinkTargetKindSchema = z.enum(LINK_TARGET_KINDS);
export const TimelineEventTypeSchema = z.enum(TIMELINE_EVENT_TYPES);
export const ActorTypeSchema = z.enum(ACTOR_TYPES);
export const SignalStatusSchema = z.enum(SIGNAL_STATUSES);
export const PublicComponentIdSchema = z.enum(PUBLIC_COMPONENT_IDS);
export const PublicStatusSchema = z.enum(PUBLIC_STATUSES);
export const PublicImpactSchema = z.enum(PUBLIC_IMPACTS);
export const PublicStateSchema = z.enum(PUBLIC_STATES);

export const FreezeVerdictSchema = z.object({
  blocked: z.boolean(),
  reason: z.string().nullable(),
  incidents: z.array(z.string()),
});

export const LiveExposureSchema = z.object({
  liveSales: z.number().int(),
  biddingOpenSales: z.number().int(),
  approvedBidders: z.number().int(),
  lotsUnsold: z.number().int(),
  blindSpots: z.array(z.string()),
});

export const GuardVerdictSchema = z.object({
  requiresOverride: z.boolean(),
  reason: z.string().nullable(),
  exposure: LiveExposureSchema,
});

// Parity: the schemas and the types describe the same thing, or this fails to compile.
const _le = (x: SchemaOf<typeof LiveExposureSchema>): LiveExposure => x;
const _gv = (x: SchemaOf<typeof GuardVerdictSchema>): GuardVerdict => x;
void _le; void _gv;
type SchemaOf<T extends z.ZodTypeAny> = z.infer<T>;
const _k: RecordKind = 'INC' satisfies SchemaOf<typeof RecordKindSchema>;
const _sev: IncidentSeverity = 'SEV1' satisfies SchemaOf<typeof IncidentSeveritySchema>;
const _is: IncidentStatus = 'NEW' satisfies SchemaOf<typeof IncidentStatusSchema>;
const _ct: ChangeType = 'NORMAL' satisfies SchemaOf<typeof ChangeTypeSchema>;
const _cs: ChangeStatus = 'DRAFT' satisfies SchemaOf<typeof ChangeStatusSchema>;
const _rs: RequestStatus = 'NEW' satisfies SchemaOf<typeof RequestStatusSchema>;
const _fv = (x: SchemaOf<typeof FreezeVerdictSchema>): FreezeVerdict => x;
void _k; void _sev; void _is; void _ct; void _cs; void _rs; void _fv;
